import {
  Box,
  Card,
  SimpleGrid,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useState } from "react";

import { BodyText } from "@/components/body-text";
import { GoonAvatar } from "@/components/goon-avatar";
import { HeaderText } from "@/components/header-text";
import { HoverCardWithLink } from "@/components/hover-card-with-link";
import { Rank } from "@/components/rank";
import { RoundedBadge } from "@/components/rounded-badge";
import { RoundedButton } from "@/components/rounded-button";
import { useGuardiansCampaignStats } from "@/hooks/useGuardiansCampaignStats";
import { useGuardiansCampaignUserStats } from "@/hooks/useGuardiansCampaignUserStats";
import { GuardiansCampaignUserStats } from "@/types";
import { truncateAddress } from "@/utils";

const RANGES = [
  { displayName: "1-10", startIndex: 0 },
  { displayName: "11-20", startIndex: 10 },
  { displayName: "21-30", startIndex: 20 },
  { displayName: "31-40", startIndex: 30 },
  { displayName: "41-50", startIndex: 40 },
  { displayName: "51-60", startIndex: 50 },
  { displayName: "61-70", startIndex: 60 },
  { displayName: "71-80", startIndex: 70 },
  { displayName: "81-90", startIndex: 80 },
  { displayName: "91-100", startIndex: 90 },
];

const PAGE_LENGTH = 10;

export const GuardiansLeaderboard = () => {
  const { guardiansCampaignStats } = useGuardiansCampaignStats();
  const [startIdx, setStartIdx] = useState(0);
  const filteredUsers =
    guardiansCampaignStats?.top100Users.slice(
      startIdx,
      startIdx + PAGE_LENGTH,
    ) ?? [];
  return (
    <VStack w="full" alignItems="start" gap={4}>
      <HeaderText>Guardians of Plume Leaderboard</HeaderText>
      <BodyText fontFamily="heading">
        Rankings and transactions are updated every few hours
      </BodyText>
      <Top100Banner />
      <Wrap gap={[2, 2, 3, 4]} w="full">
        {guardiansCampaignStats?.top100Users.length
          ? RANGES.map((range) => (
              <WrapItem key={range.startIndex}>
                <RoundedButton
                  h={12}
                  px={6}
                  onClick={() => setStartIdx(range.startIndex)}
                  color={
                    startIdx === range.startIndex ? "fg.inverted" : "fg.subtle"
                  }
                  bgColor={startIdx === range.startIndex ? "bg.inverted" : "bg"}
                  variant={startIdx === range.startIndex ? "solid" : "outline"}
                  _hover={
                    startIdx === range.startIndex
                      ? { bgColor: "gray.700" }
                      : { bgColor: "bg.muted" }
                  }
                >
                  <Text fontSize="md">{range.displayName}</Text>
                </RoundedButton>
              </WrapItem>
            ))
          : null}
      </Wrap>
      <SimpleGrid columns={[2, 3, 4, 5]} w="full" gap={4}>
        {filteredUsers.map((user) => (
          <LeaderboardPositionCard user={user} key={user.walletAddress} />
        ))}
      </SimpleGrid>
    </VStack>
  );
};

export const Top100Banner = () => {
  const { guardiansCampaignUserStats } = useGuardiansCampaignUserStats();
  const { guardiansCampaignStats } = useGuardiansCampaignStats();
  const hundredthPlaceTxCount =
    guardiansCampaignStats?.top100Users.at(-1)?.txCount ?? 0;
  const userIsInTop100 = (guardiansCampaignUserStats?.rank ?? Infinity) <= 100;
  const txReqToGetToTop100 =
    hundredthPlaceTxCount && guardiansCampaignUserStats && !userIsInTop100
      ? hundredthPlaceTxCount - guardiansCampaignUserStats?.txCount
      : undefined;

  return (
    <Card.Root
      borderRadius="2xl"
      backgroundImage={[
        "none",
        "none",
        "none",
        "none",
        "url(/assets/banner/banner-guardians-top-100-bg.avif)",
      ]}
      backgroundPosition="right"
      backgroundRepeat="no-repeat"
      backgroundSize="contain"
      px={6}
      minH={128}
      border="none"
      w="full"
      bgColor="#2A2C45"
      py={4}
    >
      <Card.Body p={0}>
        <VStack align="start" gap={1} h="full" justifyContent="center">
          <BodyText fontSize="md" color="fg.inverted" mr={[0, 0, 0, 0, 300]}>
            Top 100 campaign contributors will get{" "}
            {guardiansCampaignStats?.campaign.tiers.at(-1)?.pp} PP and the
            Guardian Infinite badge when the campaign ends
          </BodyText>
          {txReqToGetToTop100 && (
            <BodyText fontSize="md" color="#FFFFFFB2">
              Transactions Required to get into top 100:{" "}
              <BodyText fontSize="md" as="span" color="fg.inverted">
                {txReqToGetToTop100}
              </BodyText>
            </BodyText>
          )}

          <BodyText fontSize="md" color="#FFFFFFB2">
            Your rank:{" "}
            <BodyText fontSize="md" as="span" color="fg.inverted">
              {guardiansCampaignUserStats?.rank ?? "N/A"}
            </BodyText>
          </BodyText>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

const LeaderboardPositionCard = ({
  user,
}: {
  user: GuardiansCampaignUserStats;
}) => {
  return (
    <HoverCardWithLink
      href={`/profile/${user.walletAddress}`}
      cardRootStyles={{ p: 6 }}
    >
      <VStack>
        <Box position="relative" mb={6}>
          <GoonAvatar seed={user.walletAddress} boxSize="54px" />
          <RoundedBadge
            position="absolute"
            left={0}
            right={0}
            bottom={-4.5}
            justifyContent="center"
          >
            <Rank rank={user.rank} iconProps={{ boxSize: 6 }} />
          </RoundedBadge>
        </Box>
        <BodyText fontSize="lg" fontFamily="heading" color="fg">
          {truncateAddress(user.walletAddress)}
        </BodyText>
        <BodyText color="fg.muted">{user.txCount} TXs</BodyText>
      </VStack>
    </HoverCardWithLink>
  );
};
